import React, { useState, useMemo } from 'react';
import { usePermissions } from '../../hooks/usePermissions';
import { useTenant } from '../../contexts/TenantContext';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { ShieldCheck, Database, Server, Users, Activity, Settings, HardDrive, RefreshCw, AlertTriangle, MessageSquare, Monitor, CreditCard, DollarSign, ChevronLeft, ChevronRight, Search, Trash2, Download, Layers, Newspaper, Megaphone, UserCheck, AlertOctagon, UserPlus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useAdminUsers, useAdminNodes, useAdminSync, useAdminHealth, useScanOrphanedNodes, useReassignNodeOwner, usePurgeUnownedOrphanedNodes, useSyncClerkUser } from '../../hooks/useAdmin';
import { useDirectory, useUpdateMemberRole } from '../../hooks/useDirectory';
import { useLedgerTransactions } from '../../hooks/useLedger';
import { Spinner } from '../../components/ui/Spinner';
import { apiWorker } from '../../api/worker';
import { cn } from '../../lib/utils';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { AnnouncementComposer } from '../../components/pulse/AnnouncementComposer';
import { AdminBroadcastModal } from './AdminBroadcastModal';

export const AdminNodesManager = () => {
  const navigate = useNavigate();
  const { switchOrg } = useTenant();
  const { isSuperAdmin, isOperationsAdmin, isSupportAdmin } = usePermissions();
  const [page, setPage] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const { data, isLoading } = useAdminNodes(page, 12);
  const { data: orphanedNodes = [], isLoading: isLoadingOrphans } = useScanOrphanedNodes();
  const { data: adminUsersData } = useAdminUsers(0, 100);
  const reassignOwnerMutation = useReassignNodeOwner();
  const purgeUnownedMutation = usePurgeUnownedOrphanedNodes();
  const syncClerkUserMutation = useSyncClerkUser();

  const adminUsersList = useMemo(() => {
    return Array.isArray(adminUsersData) ? adminUsersData : (Array.isArray(adminUsersData?.content) ? adminUsersData.content : []);
  }, [adminUsersData]);

  const usersMap = useMemo(() => {
    const map = new Map<string, any>();
    adminUsersList.forEach((u: any) => {
      if (u.id) map.set(u.id, u);
      if (u.clerkId) map.set(u.clerkId, u);
    });
    return map;
  }, [adminUsersList]);

  const [selectedNode, setSelectedNode] = useState<any>(null);
  const [broadcastModalOpen, setBroadcastModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteConfirmInput, setDeleteConfirmInput] = useState('');
  const [purgeOrphansModalOpen, setPurgeOrphansModalOpen] = useState(false);
  const [onlyOrphansFilter, setOnlyOrphansFilter] = useState(false);
  const [reassignUserIdInput, setReassignUserIdInput] = useState('');
  const [placementUserId, setPlacementUserId] = useState('');
  const [placementRole, setPlacementRole] = useState('ADMIN');
  const [showPlacementForm, setShowPlacementForm] = useState(false);
  const queryClient = useQueryClient();

  const { data: directoryData, isLoading: isLoadingDirectory } = useDirectory(selectedNode?.id || '', { includeDescendants: false });
  const updateMemberRoleMutation = useUpdateMemberRole(selectedNode?.id || '');

  const manualPlacementMutation = useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: string }) => {
      await apiWorker.post(`/api/admin/nodes/${selectedNode.id}/members/${userId}/manual-placement`, { role });
    },
    onSuccess: () => {
      toast.success('User membership and role placed in org');
      queryClient.invalidateQueries({ queryKey: ['directory', selectedNode?.id] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'nodes'] });
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || 'Failed to place user in org')
  });

  // Local override states
  const [overrideTier, setOverrideTier] = useState('LOCAL');
  const [overrideFee, setOverrideFee] = useState(0);

  // Set default values when node changes. Done as a render-phase adjustment (the
  // previous-value guard makes setState-during-render legal) rather than an effect.
  const [prevOverrideNodeId, setPrevOverrideNodeId] = useState<string | null>(selectedNode?.id ?? null);
  if (selectedNode && prevOverrideNodeId !== selectedNode.id) {
    setPrevOverrideNodeId(selectedNode.id);
    setOverrideTier(selectedNode.tier || 'LOCAL');
    setOverrideFee(selectedNode.config?.flatFeeOverride || 0);
    setReassignUserIdInput('');
  }

  const payoutMutation = useMutation({
    mutationFn: async (frozen: boolean) => {
      await apiWorker.patch(`/api/admin/billing/nodes/${selectedNode.id}/payout-status`, { frozen });
    },
    onSuccess: (data, frozen) => {
      toast.success(frozen ? 'Payouts Frozen' : 'Payouts Resumed');
      setSelectedNode((prev: any) => ({ ...prev, payoutsFrozen: frozen }));
      queryClient.invalidateQueries({ queryKey: ['admin', 'nodes'] });
    },
    onError: () => toast.error('Failed to update payout status')
  });

  const configMutation = useMutation({
    mutationFn: async ({ tier, flatFee }: { tier: string, flatFee: number }) => {
      await apiWorker.patch(`/api/nodes/${selectedNode.id}/config`, { tier, flatFee });
    },
    onSuccess: () => {
      toast.success('Billing Configuration Overridden');
      setSelectedNode((prev: any) => ({
        ...prev,
        tier: overrideTier,
        config: { ...prev.config, flatFeeOverride: overrideFee }
      }));
      queryClient.invalidateQueries({ queryKey: ['admin', 'nodes'] });
    },
    onError: () => toast.error('Failed to override configurations')
  });

  const exportNodeMutation = useMutation({
    mutationFn: async () => {
      await apiWorker.post(`/api/admin/platform/export`, { nodeId: selectedNode.id });
    },
    onSuccess: () => toast.success('Org data export triggered in background'),
    onError: () => toast.error('Failed to export org data')
  });

  interface DeleteNodeTarget {
    id: string;
    slug: string;
  }

  const deleteNodeMutation = useMutation({
    mutationFn: async (targetNode?: DeleteNodeTarget) => {
      const nodeToDelete = targetNode || selectedNode;
      if (!nodeToDelete) return;
      await apiWorker.delete(`/api/admin/nodes/${nodeToDelete.id}`, {
        params: { confirmSlug: nodeToDelete.slug }
      });
    },
    onSuccess: (_, variables: DeleteNodeTarget | undefined) => {
      toast.success('Org Deleted');
      const deletedSlug = variables?.slug || selectedNode?.slug;
      if (deletedSlug && localStorage.getItem('active_org') === deletedSlug) {
        localStorage.removeItem('active_org');
      }
      if (!variables || variables.id === selectedNode?.id) {
        setSelectedNode(null);
        setDeleteModalOpen(false);
        setDeleteConfirmInput('');
      }
      queryClient.invalidateQueries({ queryKey: ['admin', 'nodes'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'nodes', 'orphans'] });
      queryClient.invalidateQueries({ queryKey: ['user-memberships-live'] });
      queryClient.invalidateQueries({ queryKey: ['user-memberships-mock'] });
      queryClient.invalidateQueries({ queryKey: ['memberships'] });
      queryClient.invalidateQueries({ queryKey: ['nodes'] });
      queryClient.invalidateQueries({ queryKey: ['node-config-full'] });
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || 'Failed to delete org')
  });

  if (selectedNode) {
    const creatorUser = selectedNode.createdBy ? usersMap.get(selectedNode.createdBy) : null;
    const isCreatorInDb = Boolean(creatorUser);
    const isCreatorInRoster = Boolean(
      selectedNode.createdBy &&
      directoryData?.members?.some((m: any) =>
        m.userId === selectedNode.createdBy ||
        m.id === selectedNode.createdBy ||
        (creatorUser && (m.userId === creatorUser.id || m.userId === creatorUser.clerkId || m.id === creatorUser.id))
      )
    );

    return (
      <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-300">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white dark:bg-slate-900 p-6 rounded-none border-2 border-black dark:border-white dark:border-white/80 shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF]">
          <div className="flex items-center gap-4">
            <Button variant="secondary" size="sm" onClick={() => setSelectedNode(null)} className="gap-2">
              <ChevronLeft size={16} /> Back to Organizations
            </Button>
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                {selectedNode.name}
                <Badge variant="blue" className="text-xs">{selectedNode.tier}</Badge>
              </h2>
              <p className="text-xs text-gray-500 font-mono mt-1">ID: {selectedNode.id} • Slug: {selectedNode.slug}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Badge variant={selectedNode.visibility === 'PUBLIC' ? 'green' : selectedNode.visibility === 'PRIVATE' ? 'red' : 'blue'}>
              {selectedNode.visibility || 'PUBLIC'}
            </Badge>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Subscription & Billing */}
          {(isSuperAdmin || isOperationsAdmin) && (
            <Card className="lg:col-span-2 p-6 bg-white dark:bg-slate-900 rounded-none border-2 border-black dark:border-white dark:border-white shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] space-y-6">
              <div className="border-b dark:border-white pb-4">
                <h3 className="font-bold text-base text-gray-900 dark:text-white flex items-center gap-2">
                  <CreditCard size={18} className="text-blue-500" /> Subscription & Billing Overrides
                </h3>
                <p className="text-xs text-gray-500 mt-1">Override the commercial tier and fee properties for this organization.</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block">Billing Tier</label>
                  <select 
                    className="w-full border-2 border-black dark:border-white rounded-none p-3 text-sm bg-white/50 dark:bg-slate-950 border-black dark:border-white dark:border-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
                    value={overrideTier}
                    onChange={e => setOverrideTier(e.target.value)}
                  >
                    <option value="LOCAL">LOCAL Tier</option>
                    <option value="FOUNDATION">FOUNDATION Tier</option>
                    <option value="MOMENTUM">MOMENTUM Tier</option>
                    <option value="INSTITUTIONAL">INSTITUTIONAL Tier</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block">Flat Fee Override ($)</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                    <input 
                      type="number" 
                      className="w-full border-2 border-black dark:border-white rounded-none p-3 pl-8 text-sm bg-white/50 dark:bg-slate-950 border-black dark:border-white dark:border-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition" 
                      placeholder="e.g. 100" 
                      value={overrideFee}
                      onChange={e => setOverrideFee(Number(e.target.value))}
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end pt-2 border-t dark:border-white">
                <Button 
                  variant="primary" 
                  className="bg-blue-600 hover:bg-blue-700 text-white font-medium shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] shadow-blue-500/10 px-6 py-2.5 rounded-none transition-all"
                  onClick={() => configMutation.mutate({ tier: overrideTier, flatFee: overrideFee })}
                  disabled={configMutation.isPending}
                >
                  Apply Configuration Overrides
                </Button>
              </div>

              <div className="pt-6 border-t dark:border-white flex items-center justify-between bg-orange-50/20 dark:bg-orange-950/10 p-4 rounded-none border-2 border-black dark:border-white border-orange-100/50 dark:border-orange-900/30">
                <div className="space-y-1 pr-4">
                  <h4 className="text-sm font-semibold text-orange-800 dark:text-orange-300">Freeze Payouts (Stripe Connect)</h4>
                  <p className="text-xs text-orange-600/80 dark:text-orange-400/80">Temporarily freeze Stripe Connect payouts for this org. Useful for compliance disputes.</p>
                </div>
                <input 
                  type="checkbox" 
                  className="toggle" 
                  checked={selectedNode.payoutsFrozen || false}
                  onChange={(e) => payoutMutation.mutate(e.target.checked)} 
                  disabled={payoutMutation.isPending} 
                />
              </div>
            </Card>
          )}

          {/* Org Ownership & Creator Governance */}
          {isSuperAdmin && (
            <Card className="lg:col-span-2 p-6 bg-white dark:bg-slate-900 rounded-none border-2 border-black dark:border-white shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] space-y-6">
                <div className="border-b dark:border-white pb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <div>
                    <h3 className="font-bold text-base text-gray-900 dark:text-white flex items-center gap-2">
                      <ShieldCheck size={18} className="text-blue-500" /> Org Ownership & Creator Governance
                    </h3>
                    <p className="text-xs text-gray-500 mt-1">Manage org owner assignment and remediate unowned or unsynced creator status.</p>
                  </div>
                  <Badge variant={!selectedNode.createdBy ? 'red' : isCreatorInDb ? 'green' : 'amber'}>
                    {!selectedNode.createdBy ? 'Orphaned (No Owner)' : isCreatorInDb ? 'Synced Owner' : 'Unsynced in DB'}
                  </Badge>
                </div>

                <div className="space-y-4">
                  <div className="p-3.5 bg-gray-50 dark:bg-slate-950 border-2 border-black dark:border-white font-mono text-xs flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="space-y-1">
                      <span className="text-gray-500 block uppercase text-[10px]">Current Creator / Owner</span>
                      {selectedNode.createdBy ? (
                        isCreatorInDb ? (
                          <div>
                            <span className="font-bold text-gray-900 dark:text-white font-sans text-sm block">
                              {creatorUser.fullName || creatorUser.email}
                            </span>
                            <span className="text-[11px] text-gray-400 font-mono block">
                              Email: {creatorUser.email} • ID: {selectedNode.createdBy}
                            </span>
                          </div>
                        ) : (
                          <div>
                            <span className="font-bold text-amber-600 dark:text-amber-400 font-mono text-sm block">
                              {selectedNode.createdBy}
                            </span>
                            <span className="text-[11px] text-red-500 font-sans block mt-0.5">
                              ⚠️ Clerk ID only — User account does not exist in database users collection.
                            </span>
                          </div>
                        )
                      ) : (
                        <span className="font-bold text-red-600 dark:text-red-400 font-mono">
                          None (Orphaned Org)
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      {selectedNode.createdBy && !isCreatorInDb && (
                        <Button
                          variant="secondary"
                          size="sm"
                          className="text-xs font-bold uppercase rounded-none border-2 border-black dark:border-white bg-[#FFD600] hover:bg-yellow-400 text-black shadow-[2px_2px_0px_#000000] gap-1.5"
                          onClick={() => syncClerkUserMutation.mutate(selectedNode.createdBy)}
                          disabled={syncClerkUserMutation.isPending}
                          isLoading={syncClerkUserMutation.isPending}
                        >
                          <RefreshCw size={13} className={cn(syncClerkUserMutation.isPending ? "animate-spin" : "")} />
                          Resync User to DB
                        </Button>
                      )}
                      {selectedNode.memberCount !== undefined && (
                        <span className="text-xs text-gray-500 font-sans">
                          {selectedNode.memberCount} member(s)
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="space-y-3 pt-2">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1">
                      <label className="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider block">
                        Reassign Owner to Clerk User ID
                      </label>
                      {adminUsersList.length > 0 && (
                        <div className="flex items-center gap-1 text-[11px] font-sans text-gray-500">
                          <span>Quick assign:</span>
                          <select
                            className="border border-black dark:border-white text-xs p-1 bg-white dark:bg-slate-900 rounded-none font-mono"
                            onChange={(e) => {
                              if (e.target.value) setReassignUserIdInput(e.target.value);
                            }}
                            defaultValue=""
                          >
                            <option value="" disabled>Select active DB user...</option>
                            {adminUsersList.map((u: any) => {
                              const assignVal = u.clerkId || u.id;
                              return (
                                <option key={u.id} value={assignVal}>
                                  {u.fullName || u.email} ({assignVal ? assignVal.slice(0, 12) + '...' : ''})
                                </option>
                              );
                            })}
                          </select>
                        </div>
                      )}
                    </div>

                    <div className="flex flex-col sm:flex-row gap-2">
                      <input
                        type="text"
                        placeholder="e.g. user_2n8k3v9..."
                        className="flex-1 border-2 border-black dark:border-white rounded-none p-2.5 text-xs font-mono bg-white dark:bg-slate-950 text-black dark:text-white outline-none focus:ring-2 focus:ring-blue-500"
                        value={reassignUserIdInput}
                        onChange={(e) => setReassignUserIdInput(e.target.value)}
                      />
                      <Button
                        variant="primary"
                        size="sm"
                        className="bg-black hover:bg-[#0044FF] text-white rounded-none border-2 border-black font-bold uppercase tracking-wider text-xs px-5"
                        disabled={!reassignUserIdInput.trim() || reassignOwnerMutation.isPending}
                        onClick={() => {
                          reassignOwnerMutation.mutate(
                            { nodeId: selectedNode.id, targetUserId: reassignUserIdInput.trim() },
                            {
                              onSuccess: (updatedNode: any) => {
                                setSelectedNode((prev: any) => ({
                                  ...prev,
                                  createdBy: reassignUserIdInput.trim(),
                                  ...(updatedNode || {})
                                }));
                                setReassignUserIdInput('');
                              }
                            }
                          );
                        }}
                      >
                        {reassignOwnerMutation.isPending ? <Spinner size="sm" /> : 'Reassign Owner'}
                      </Button>
                    </div>
                    <p className="text-[11px] text-gray-500">
                      Reassigning owner designates target user as creator and grants ADMIN authority in this organization.
                    </p>
                  </div>
                </div>
              </Card>
            )}

          {/* Org Member Roster & Assigned Users */}
          <Card className="lg:col-span-3 p-6 bg-white dark:bg-slate-900 rounded-none border-2 border-black dark:border-white shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] space-y-4">
            <div className="border-b dark:border-white pb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <h3 className="font-bold text-base text-gray-900 dark:text-white flex items-center gap-2">
                  <Users size={18} className="text-purple-500" /> Org Member Roster & Assigned Users
                  <Badge variant="blue" className="text-xs font-mono">
                    {directoryData?.members?.length || 0} Member{directoryData?.members?.length === 1 ? '' : 's'}
                  </Badge>
                </h3>
                <p className="text-xs text-gray-500 mt-1">Users actively placed in this org with membership and permissions.</p>
              </div>

              <div className="flex items-center gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    const targetSlug = selectedNode.slug || selectedNode.id;
                    if (targetSlug) {
                      switchOrg(targetSlug, '/dashboard');
                    }
                  }}
                  className="text-xs font-bold uppercase tracking-wider rounded-none border-2 border-black dark:border-white"
                >
                  Dive into Org Workspace →
                </Button>
                {isSuperAdmin && (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => setShowPlacementForm(!showPlacementForm)}
                    className="text-xs font-bold uppercase tracking-wider rounded-none border-2 border-black bg-black text-white hover:bg-[#0044FF]"
                  >
                    {showPlacementForm ? 'Close Placement' : '+ Add User to Org'}
                  </Button>
                )}
              </div>
            </div>

            {/* Creator Missing Membership Alert */}
            {selectedNode.createdBy && !isCreatorInRoster && (
              <div className="p-3.5 bg-amber-50 dark:bg-amber-950/30 border-2 border-amber-500 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="space-y-0.5">
                  <div className="flex items-center gap-2 font-bold text-xs text-amber-800 dark:text-amber-300 uppercase tracking-wider">
                    <AlertTriangle size={14} className="text-amber-600 shrink-0" />
                    Creator Missing Active Membership
                  </div>
                  <p className="text-xs text-amber-700 dark:text-amber-400">
                    Org creator (<code className="font-mono">{selectedNode.createdBy}</code>) does not have an active membership role in this org roster.
                    {!isCreatorInDb && ' User also needs identity sync from Clerk into database.'}
                  </p>
                </div>
                {isSuperAdmin && (
                  <Button
                    variant="secondary"
                    size="sm"
                    className="text-xs font-bold uppercase rounded-none border-2 border-black dark:border-white bg-[#FFD600] hover:bg-yellow-400 text-black shrink-0"
                    onClick={async () => {
                      if (!isCreatorInDb) {
                        await syncClerkUserMutation.mutateAsync(selectedNode.createdBy);
                      }
                      manualPlacementMutation.mutate({
                        userId: creatorUser?.id || selectedNode.createdBy,
                        role: 'ADMIN'
                      });
                    }}
                    disabled={manualPlacementMutation.isPending || syncClerkUserMutation.isPending}
                    isLoading={manualPlacementMutation.isPending || syncClerkUserMutation.isPending}
                  >
                    {!isCreatorInDb ? 'Sync to DB & Grant ADMIN' : 'Grant Creator ADMIN Role'}
                  </Button>
                )}
              </div>
            )}

            {/* Manual Placement Form */}
            {showPlacementForm && isSuperAdmin && (
              <div className="p-4 bg-gray-50 dark:bg-slate-950 border-2 border-black dark:border-white space-y-3">
                <h4 className="font-bold text-xs uppercase tracking-wider text-gray-700 dark:text-gray-300">
                  Manually Place User into this Org
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="sm:col-span-2 space-y-1">
                    <label className="text-[10px] font-bold text-gray-500 uppercase block">User ID (or select from database)</label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        placeholder="User ID or Clerk ID (e.g. user_...)"
                        value={placementUserId}
                        onChange={(e) => setPlacementUserId(e.target.value)}
                        className="flex-1 p-2 text-xs font-mono border-2 border-black dark:border-white bg-white dark:bg-slate-900 rounded-none outline-none"
                      />
                      {adminUsersList.length > 0 && (
                        <select
                          className="border-2 border-black dark:border-white text-xs p-1 bg-white dark:bg-slate-900 rounded-none font-mono"
                          onChange={(e) => {
                            if (e.target.value) setPlacementUserId(e.target.value);
                          }}
                          defaultValue=""
                        >
                          <option value="" disabled>Select user...</option>
                          {adminUsersList.map((u: any) => {
                            const assignVal = u.clerkId || u.id;
                            return (
                              <option key={u.id} value={assignVal}>
                                {u.fullName || u.email} ({assignVal ? assignVal.slice(0, 12) + '...' : ''})
                              </option>
                            );
                          })}
                        </select>
                      )}
                    </div>
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-gray-500 uppercase block">Role in Org</label>
                    <select
                      value={placementRole}
                      onChange={(e) => setPlacementRole(e.target.value)}
                      className="w-full p-2 text-xs font-mono border-2 border-black dark:border-white bg-white dark:bg-slate-900 rounded-none outline-none"
                    >
                      <option value="ADMIN">ADMIN</option>
                      <option value="MEMBER">MEMBER</option>
                      <option value="MODERATOR">MODERATOR</option>
                    </select>
                  </div>
                </div>
                <div className="flex justify-end gap-2 pt-1">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setShowPlacementForm(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    className="bg-black hover:bg-[#0044FF] text-white rounded-none border-2 border-black font-bold uppercase text-xs"
                    disabled={!placementUserId.trim() || manualPlacementMutation.isPending}
                    isLoading={manualPlacementMutation.isPending}
                    onClick={() => {
                      manualPlacementMutation.mutate(
                        { userId: placementUserId.trim(), role: placementRole },
                        {
                          onSuccess: () => {
                            setPlacementUserId('');
                            setShowPlacementForm(false);
                          }
                        }
                      );
                    }}
                  >
                    Assign Member
                  </Button>
                </div>
              </div>
            )}

            {/* Member Directory List / Table */}
            {isLoadingDirectory ? (
              <div className="flex justify-center p-8"><Spinner size="md" /></div>
            ) : !Array.isArray(directoryData?.members) || directoryData.members.length === 0 ? (
              <div className="p-8 text-center border-2 border-dashed border-gray-300 dark:border-gray-700 space-y-2">
                <Users size={28} className="mx-auto text-gray-400" />
                <p className="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  No Members Assigned to this Org
                </p>
                <p className="text-[11px] text-gray-500 max-w-sm mx-auto">
                  This org has zero active membership records. Use "+ Add User to Org" or Reassign Owner above to establish leadership.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto border-2 border-black dark:border-white">
                <table className="w-full text-left text-xs font-mono">
                  <thead className="bg-gray-100 dark:bg-slate-800 border-b-2 border-black dark:border-white uppercase text-[10px] tracking-wider text-gray-600 dark:text-gray-300">
                    <tr>
                      <th className="p-2.5">Member</th>
                      <th className="p-2.5">User ID / Identity</th>
                      <th className="p-2.5">Role</th>
                      <th className="p-2.5">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-slate-800 bg-white dark:bg-slate-950">
                    {directoryData.members.map((m: any) => {
                      const isNodeCreator = 
                        m.userId === selectedNode.createdBy || 
                        m.id === selectedNode.createdBy ||
                        (creatorUser && (m.userId === creatorUser.id || m.userId === creatorUser.clerkId || m.id === creatorUser.id));
                      return (
                        <tr key={m.id || m.userId} className="hover:bg-gray-50 dark:hover:bg-slate-900/50">
                          <td className="p-2.5 font-sans font-medium text-gray-900 dark:text-white">
                            <div className="flex items-center gap-2">
                              <span>{m.name || m.fullName || m.email || 'Unnamed User'}</span>
                              {isNodeCreator && (
                                <Badge variant="amber" className="text-[9px] uppercase font-mono tracking-widest">
                                  CREATOR
                                </Badge>
                              )}
                            </div>
                            {m.email && (
                              <span className="text-[10px] text-gray-500 font-mono block">{m.email}</span>
                            )}
                          </td>
                          <td className="p-2.5 text-[11px] text-gray-500 truncate max-w-[160px]" title={m.userId || m.id}>
                            {m.userId || m.id}
                          </td>
                          <td className="p-2.5">
                            {isSuperAdmin ? (
                              <select
                                value={m.role || 'MEMBER'}
                                disabled={updateMemberRoleMutation.isPending}
                                onChange={(e) => {
                                  updateMemberRoleMutation.mutate({
                                    userId: m.userId || m.id,
                                    role: e.target.value
                                  });
                                }}
                                className="text-[10px] font-mono font-bold uppercase border border-black dark:border-white bg-white dark:bg-slate-900 px-1.5 py-0.5 rounded-none outline-none cursor-pointer"
                              >
                                <option value="ADMIN">ADMIN</option>
                                <option value="MODERATOR">MODERATOR</option>
                                <option value="MEMBER">MEMBER</option>
                              </select>
                            ) : (
                              <Badge variant={m.role === 'ADMIN' ? 'red' : m.role === 'MODERATOR' ? 'purple' : 'blue'} className="text-[10px] uppercase font-mono">
                                {m.role || 'MEMBER'}
                              </Badge>
                            )}
                          </td>
                          <td className="p-2.5 text-[11px] text-gray-500">
                            {m.status || 'ACTIVE'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          {/* Data & Admin Operations */}
          <Card className="p-6 bg-white dark:bg-slate-900 rounded-none border-2 border-black dark:border-white dark:border-white shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] space-y-6">
            <div className="border-b dark:border-white pb-4">
              <h3 className="font-bold text-base text-gray-900 dark:text-white flex items-center gap-2">
                <Settings size={18} className="text-purple-500" /> Org Administration
              </h3>
              <p className="text-xs text-gray-500 mt-1">Administrative actions and data compliance commands.</p>
            </div>

            <div className="space-y-3">
              {(isSuperAdmin || isOperationsAdmin) && (
                <Button 
                  variant="secondary" 
                  className="w-full text-xs font-semibold py-3 justify-center gap-2 rounded-none" 
                  onClick={() => exportNodeMutation.mutate()}
                  disabled={exportNodeMutation.isPending}
                >
                  <Download size={14} /> Bulk Data Exporter
                </Button>
              )}
              {isSuperAdmin && (
                <Button 
                  variant="secondary" 
                  className="w-full text-xs font-semibold py-3 justify-center gap-2 rounded-none bg-amber-50 hover:bg-amber-100/80 text-amber-900 border-amber-200 dark:bg-amber-950/20 dark:border-amber-900/30 dark:text-amber-300" 
                  onClick={() => setBroadcastModalOpen(true)}
                >
                  <Megaphone size={14} /> Send Org Broadcast
                </Button>
              )}
              {isSuperAdmin && (
                <div className="pt-4 border-t dark:border-white space-y-2">
                  <h4 className="text-xs font-bold text-red-500 uppercase tracking-wider">Danger Zone</h4>
                  <Button 
                    variant="secondary" 
                    className="w-full text-xs font-semibold py-3 justify-center gap-2 bg-red-50 hover:bg-red-100/80 text-red-600 border-red-100 dark:bg-red-950/20 dark:border-red-900/30 dark:text-red-400 dark:hover:bg-red-950/30 rounded-none" 
                    onClick={() => {
                      setDeleteConfirmInput('');
                      setDeleteModalOpen(true);
                    }}
                    disabled={deleteNodeMutation.isPending}
                  >
                    <Trash2 size={14} /> Delete Org Permanently
                  </Button>
                </div>
              )}
            </div>
          </Card>
        </div>
        <AdminBroadcastModal
          isOpen={broadcastModalOpen}
          onClose={() => setBroadcastModalOpen(false)}
          defaultNodeId={selectedNode.id}
          defaultNodeName={selectedNode.name}
        />
        <Modal
          isOpen={deleteModalOpen}
          onClose={() => {
            if (!deleteNodeMutation.isPending) {
              setDeleteModalOpen(false);
              setDeleteConfirmInput('');
            }
          }}
          title="Delete Org Permanently"
          className="border-2 border-black dark:border-white shadow-[8px_8px_0px_#000000] dark:shadow-[8px_8px_0px_#FFFFFF] rounded-none max-w-lg"
          footer={
            <div className="flex items-center justify-end gap-3 w-full">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  setDeleteModalOpen(false);
                  setDeleteConfirmInput('');
                }}
                disabled={deleteNodeMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                size="sm"
                onClick={() => deleteNodeMutation.mutate(selectedNode ? { id: selectedNode.id, slug: selectedNode.slug } : undefined)}
                disabled={
                  (!deleteConfirmInput.trim() || 
                  (deleteConfirmInput.trim() !== selectedNode?.slug && 
                   deleteConfirmInput.trim() !== `CONFIRM DELETE ${selectedNode?.slug}`)) || 
                  deleteNodeMutation.isPending
                }
                isLoading={deleteNodeMutation.isPending}
              >
                Permanently Delete
              </Button>
            </div>
          }
        >
          <div className="p-6 space-y-4">
            <div className="p-4 bg-red-50 dark:bg-red-950/30 border-2 border-red-500 text-red-700 dark:text-red-300 space-y-2">
              <div className="flex items-center gap-2 font-bold uppercase text-xs tracking-wider">
                <AlertTriangle size={16} className="text-red-500" />
                Warning: Irreversible Action
              </div>
              <p className="text-xs">
                This action will permanently delete <strong className="font-bold underline">{selectedNode?.name}</strong> and remove all associated child orgs, configurations, and ledger records.
              </p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="block text-xs font-mono font-bold uppercase tracking-wider text-gray-700 dark:text-gray-300">
                  To confirm deletion, enter org slug:
                </label>
                <button
                  type="button"
                  onClick={() => setDeleteConfirmInput(selectedNode?.slug || '')}
                  className="text-[11px] font-mono text-blue-600 dark:text-blue-400 hover:underline cursor-pointer"
                >
                  Click to autofill
                </button>
              </div>
              <div 
                onClick={() => setDeleteConfirmInput(selectedNode?.slug || '')}
                className="p-3 bg-gray-100 dark:bg-slate-800 border border-black/20 dark:border-white/20 font-mono text-xs select-all flex items-center justify-between cursor-pointer hover:bg-gray-200 dark:hover:bg-slate-700 transition"
                title="Click to autofill input"
              >
                <span className="font-bold text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-950/60 px-1.5 py-0.5 border border-red-300 dark:border-red-800">
                  {selectedNode?.slug}
                </span>
                <span className="text-[10px] text-gray-500 uppercase tracking-widest font-sans ml-2">(click to fill)</span>
              </div>
            </div>

            <div className="space-y-1">
              <input
                type="text"
                value={deleteConfirmInput}
                onChange={(e) => setDeleteConfirmInput(e.target.value)}
                placeholder={selectedNode?.slug || 'Enter org slug'}
                className="w-full px-3 py-2 text-xs font-mono border-2 border-black dark:border-white bg-white dark:bg-black text-black dark:text-white rounded-none focus:outline-none focus:ring-2 focus:ring-red-500"
                autoFocus
              />
              {deleteConfirmInput && 
               deleteConfirmInput.trim() !== selectedNode?.slug && 
               deleteConfirmInput.trim() !== `CONFIRM DELETE ${selectedNode?.slug}` && (
                <p className="text-[11px] text-red-500 font-mono">
                  Input does not match target slug: <span className="font-bold">{selectedNode?.slug}</span>
                </p>
              )}
              {(deleteConfirmInput.trim() === selectedNode?.slug || 
                deleteConfirmInput.trim() === `CONFIRM DELETE ${selectedNode?.slug}`) && (
                <p className="text-[11px] text-green-600 dark:text-green-400 font-mono font-bold">
                  ✓ Org slug matched
                </p>
              )}
            </div>
          </div>
        </Modal>
      </div>
    );
  }

  const safeOrphanedNodes = Array.isArray(orphanedNodes) 
    ? orphanedNodes 
    : (Array.isArray((orphanedNodes as any)?.content) ? (orphanedNodes as any).content : []);
  const orphanedNodeIdSet = new Set(safeOrphanedNodes.map((o: any) => o.id));

  const nodeList = Array.isArray(data) ? data : (Array.isArray(data?.content) ? data.content : []);
  const baseNodes = onlyOrphansFilter ? safeOrphanedNodes : nodeList;
  const filteredNodes = baseNodes.filter((n: any) => 
    n.name?.toLowerCase().includes(searchQuery.toLowerCase()) || 
    n.slug?.toLowerCase().includes(searchQuery.toLowerCase()) || 
    n.id?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Server size={20} className="text-blue-500" /> Platform Organizations
          </h2>
          <p className="text-xs text-gray-500">Overview and configuration of all tenant orgs in the system.</p>
        </div>

        <div className="flex items-center gap-2 w-full sm:w-72">
          <div className="relative w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
            <input 
              type="text" 
              placeholder="Search by name or slug..." 
              className="w-full pl-9 pr-4 py-2 text-xs border-2 border-black dark:border-white rounded-none bg-white dark:bg-slate-900 border-black dark:border-white dark:border-white focus:ring-2 focus:ring-blue-500 outline-none transition"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Orphan Alert Banner */}
      {orphanedNodes.length > 0 && (
        <div className="p-4 bg-[#FFD600]/20 dark:bg-[#FFD600]/10 border-4 border-black dark:border-[#FFD600] shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFD600] space-y-3">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-start sm:items-center gap-3">
              <div className="p-2 bg-[#FFD600] text-black border-2 border-black shrink-0">
                <AlertTriangle size={20} />
              </div>
              <div>
                <h3 className="font-black uppercase tracking-tight text-sm text-gray-900 dark:text-white">
                  {orphanedNodes.length} Orphaned Organization{orphanedNodes.length === 1 ? '' : 's'} Detected
                </h3>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  Organization(s) created without valid owner account synchronization. Reassign an active owner or purge unowned organizations with zero members.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setOnlyOrphansFilter(!onlyOrphansFilter)}
                className={cn(
                  "text-xs font-bold uppercase tracking-wider rounded-none border-2 border-black dark:border-white",
                  onlyOrphansFilter ? "bg-[#0044FF] text-white hover:bg-blue-700" : ""
                )}
              >
                {onlyOrphansFilter ? 'Show All Organizations' : `Show Orphans (${orphanedNodes.length})`}
              </Button>
              {isSuperAdmin && (
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => setPurgeOrphansModalOpen(true)}
                  className="text-xs font-bold uppercase tracking-wider rounded-none border-2 border-black dark:border-white bg-[#FF0000] hover:bg-red-700 text-white"
                >
                  <Trash2 size={13} className="mr-1.5 inline" />
                  Purge Unowned (0 Members)
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-16"><Spinner size="lg" /></div>
      ) : (
        <div className="space-y-6">
          {filteredNodes.length === 0 ? (
            <Card className="flex flex-col items-center justify-center p-12 text-center border-2 border-black bg-white dark:bg-slate-900 rounded-none">
              <Server size={36} className="text-gray-300 dark:text-slate-700 mb-3" />
              <h3 className="font-semibold text-gray-700 dark:text-gray-300">No orgs found</h3>
              <p className="text-xs text-gray-400 max-w-xs mt-1">Could not find any orgs matching your search or page selection.</p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredNodes.map((n: any) => {
                const isOrphan = orphanedNodeIdSet.has(n.id) || !n.createdBy;
                return (
                  <div 
                    key={n.id} 
                    className={cn(
                      "p-5 bg-white dark:bg-slate-900 border-2 rounded-none shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] cursor-pointer transition-all duration-200 flex flex-col justify-between",
                      isOrphan 
                        ? "border-red-600 hover:border-red-700 dark:border-red-500" 
                        : "border-black dark:border-white hover:border-blue-500/50 dark:hover:border-blue-500/40"
                    )}
                    onClick={() => setSelectedNode(n)}
                  >
                    <div className="space-y-2">
                      <div className="flex items-start justify-between gap-2">
                        <h4 className="font-bold text-gray-900 dark:text-white group-hover:text-blue-500 transition line-clamp-1">{n.name}</h4>
                        <div className="flex items-center gap-1">
                          {isOrphan && (
                            <Badge variant="red" className="text-[9px] uppercase tracking-wider font-mono">
                              ORPHAN
                            </Badge>
                          )}
                          <Badge variant={n.visibility === 'PUBLIC' ? 'green' : n.visibility === 'PRIVATE' ? 'red' : 'blue'} className="text-[9px] uppercase tracking-wider">
                            {n.visibility || 'PUBLIC'}
                          </Badge>
                        </div>
                      </div>
                      <p className="text-[10px] text-gray-400 font-mono line-clamp-1">Slug: {n.slug}</p>
                      <p className="text-[10px] text-gray-400 font-mono">ID: {n.id}</p>
                      {n.createdBy ? (
                        (() => {
                          const creatorUser = usersMap.get(n.createdBy);
                          return creatorUser ? (
                            <p className="text-[10px] text-gray-400 font-mono truncate" title={n.createdBy}>
                              Creator: <span className="text-gray-700 dark:text-gray-300 font-sans font-medium">{creatorUser.fullName || creatorUser.email}</span>
                            </p>
                          ) : (
                            <div className="flex items-center justify-between text-[10px] font-mono">
                              <span className="text-amber-600 dark:text-amber-400 truncate" title={n.createdBy}>
                                Creator: {n.createdBy} (Unsynced)
                              </span>
                              {isSuperAdmin && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    syncClerkUserMutation.mutate(n.createdBy);
                                  }}
                                  disabled={syncClerkUserMutation.isPending}
                                  className="text-blue-600 dark:text-blue-400 font-sans font-bold uppercase text-[9px] hover:underline ml-1 shrink-0"
                                >
                                  Sync
                                </button>
                              )}
                            </div>
                          );
                        })()
                      ) : (
                        <p className="text-[10px] text-red-600 dark:text-red-400 font-mono font-bold">
                          ⚠️ No Owner (Orphaned)
                        </p>
                      )}
                    </div>

                    <div className="flex items-center justify-between border-t dark:border-white pt-3 mt-4">
                      <div className="flex items-center gap-1 text-[11px] text-gray-500">
                        <Layers size={12} className="text-gray-400" />
                        <span>{n.tier || 'LOCAL'}</span>
                      </div>
                      <div className="text-[10px] font-semibold text-blue-600 dark:text-blue-400 flex items-center gap-1 hover:underline">
                        Manage Settings →
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {!onlyOrphansFilter && data && data.totalPages > 1 && (
            <div className="flex items-center justify-between border-t dark:border-white pt-4 mt-6">
              <span className="text-xs text-gray-500">
                Page <span className="font-semibold text-gray-700 dark:text-gray-300">{page + 1}</span> of <span className="font-semibold text-gray-700 dark:text-gray-300">{data.totalPages}</span>
              </span>
              <div className="flex gap-2">
                <Button 
                  variant="secondary" 
                  size="sm" 
                  disabled={page === 0} 
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  className="gap-1.5 px-3"
                >
                  <ChevronLeft size={14} /> Prev
                </Button>
                <Button 
                  variant="secondary" 
                  size="sm" 
                  disabled={page >= data.totalPages - 1} 
                  onClick={() => setPage(p => p + 1)}
                  className="gap-1.5 px-3"
                >
                  Next <ChevronRight size={14} />
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Purge Unowned Modal */}
      <Modal
        isOpen={purgeOrphansModalOpen}
        onClose={() => setPurgeOrphansModalOpen(false)}
        title="Purge Unowned Orphaned Organizations"
        className="border-2 border-black dark:border-white shadow-[8px_8px_0px_#000000] dark:shadow-[8px_8px_0px_#FFFFFF] rounded-none max-w-lg"
        footer={
          <div className="flex items-center justify-end gap-3 w-full">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setPurgeOrphansModalOpen(false)}
              disabled={purgeUnownedMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={() => {
                purgeUnownedMutation.mutate(undefined, {
                  onSuccess: () => {
                    setPurgeOrphansModalOpen(false);
                  }
                });
              }}
              disabled={purgeUnownedMutation.isPending || orphanedNodes.filter(o => o.memberCount === 0).length === 0}
              isLoading={purgeUnownedMutation.isPending}
            >
              Confirm Purge
            </Button>
          </div>
        }
      >
        <div className="p-6 space-y-4">
          <div className="p-4 bg-red-50 dark:bg-red-950/30 border-2 border-red-500 text-red-700 dark:text-red-300 space-y-2">
            <div className="flex items-center gap-2 font-bold uppercase text-xs tracking-wider">
              <AlertTriangle size={16} className="text-red-500" />
              Cascade Delete Unowned Organizations
            </div>
            <p className="text-xs">
              This will cascade delete all organizations that have no valid creator account and 0 active members.
            </p>
          </div>
          <div className="space-y-2">
            <span className="text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-300 block">
              Affected Orphan Organizations ({safeOrphanedNodes.filter((o: any) => o.memberCount === 0).length}):
            </span>
            <div className="max-h-60 overflow-y-auto border-2 border-black dark:border-white divide-y divide-black/10 dark:divide-white/10 p-2 bg-gray-50 dark:bg-slate-950">
              {safeOrphanedNodes.filter((o: any) => o.memberCount === 0).length === 0 ? (
                <p className="text-xs text-gray-500 p-2">No unowned orgs with 0 members found.</p>
              ) : (
                safeOrphanedNodes.filter((o: any) => o.memberCount === 0).map((node: any) => (
                  <div key={node.id} className="p-2.5 flex items-center justify-between text-xs gap-3">
                    <div 
                      className="font-mono space-y-0.5 min-w-0 cursor-pointer hover:opacity-80"
                      onClick={() => {
                        setSelectedNode(node);
                        setPurgeOrphansModalOpen(false);
                      }}
                      title="Click to view and reassign owner"
                    >
                      <div className="font-bold text-gray-900 dark:text-white truncate flex items-center gap-1.5">
                        {node.name}
                        <span className="text-[10px] text-blue-600 dark:text-blue-400 font-sans uppercase font-normal">(Reassign →)</span>
                      </div>
                      <div className="text-[10px] text-gray-500 truncate">slug: {node.slug} • id: {node.id}</div>
                    </div>
                    {isSuperAdmin && (
                      <Button
                        variant="secondary"
                        size="sm"
                        className="text-[10px] font-mono px-2 py-1 h-auto text-red-600 border border-red-300 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-950/40 shrink-0"
                        onClick={() => deleteNodeMutation.mutate({ id: node.id, slug: node.slug })}
                        disabled={deleteNodeMutation.isPending}
                      >
                        Delete Single
                      </Button>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};
